// VERA-825 / VERA-246: Address picker for the tag shop checkout.
// A lightweight selectable list of the company's saved addresses. Selecting a
// row and confirming updates the checkout's chosen shipping address (which then
// re-triggers the calculate call). Links to the address book to add a new one.

import React, {useEffect, useState} from 'react';

import {Stack, Typography, Box, useTheme} from '@mui/material';
import {Button, Modal} from '@repo/ui';
import {CheckIcon, PlusIcon} from 'lucide-react';

import {ADDRESS_TYPE_LABELS} from 'types/Address';
import type {Address} from 'types';
import {getAddressIcon} from 'utils/addressIcons';

interface ChangeAddressModalProps {
  open: boolean;
  onClose(): void;
  addresses: Address[];
  selectedId: string | null;
  onSelect(address: Address): void;
  onAddNew(): void;
}

const formatAddressLine = (address: Address): string =>
  [
    [address.city, address.state].filter(Boolean).join(', '),
    address.postal_code,
    address.country,
  ]
    .filter(Boolean)
    .join(' ');

export const ChangeAddressModal = ({
  open,
  onClose,
  addresses,
  selectedId,
  onSelect,
  onAddNew,
}: ChangeAddressModalProps) => {
  const theme = useTheme();
  const [pendingId, setPendingId] = useState<string | null>(selectedId);

  // Re-preselect the current address every time the modal opens (item-page
  // modal preselect rule — key the effect on `open`, not just the prop).
  useEffect(() => {
    if (open) setPendingId(selectedId);
  }, [open, selectedId]);

  const handleConfirm = () => {
    const chosen = addresses.find((a) => a.id === pendingId);
    if (chosen) onSelect(chosen);
    onClose();
  };

  return (
    <Modal
      title="Change shipping address"
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!pendingId}
          >
            Use this address
          </Button>
        </>
      }
    >
      <Stack spacing={3}>
        {addresses.map((address) => {
          const isSelected = address.id === pendingId;
          return (
            <Box
              key={address.id}
              role="button"
              tabIndex={0}
              onClick={() => setPendingId(address.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setPendingId(address.id);
                }
              }}
              sx={{
                cursor: 'pointer',
                p: 3,
                borderRadius: 2,
                border: `1px solid ${
                  isSelected
                    ? theme.palette.brand[600]
                    : theme.palette.primary[100]
                }`,
                backgroundColor: isSelected
                  ? theme.palette.brand['600-6%']
                  : 'transparent',
                transition: 'all 0.15s ease-in',
                '&:hover': {
                  backgroundColor: theme.palette.brand['600-6%'],
                },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={3}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={3}
                  minWidth={0}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: `1px solid ${theme.palette.primary[100]}`,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {(() => {
                      const Icon = getAddressIcon(address.type, address.icon);
                      return <Icon size={20} strokeWidth={1} />;
                    })()}
                  </Box>
                  <Stack minWidth={0}>
                    <Typography variant="body2" noWrap>
                      {address.label || ADDRESS_TYPE_LABELS[address.type]}
                    </Typography>
                    <Typography
                      variant="body3"
                      color="text.secondary"
                      noWrap
                    >
                      {formatAddressLine(address)}
                    </Typography>
                  </Stack>
                </Stack>
                {isSelected && (
                  <CheckIcon
                    size={20}
                    strokeWidth={2}
                    color={theme.palette.common.white}
                  />
                )}
              </Stack>
            </Box>
          );
        })}

        <Button
          variant="text"
          size="large"
          startIcon={<PlusIcon size={18} strokeWidth={1} />}
          onClick={onAddNew}
          sx={{alignSelf: 'flex-start'}}
        >
          Add a new address
        </Button>
      </Stack>
    </Modal>
  );
};
